import React from 'react';
import FormField from './FormField';

// Mirrors raiseError in server/services/appraisalRaise.js: the same AMOUNT_PATTERN and
// MAX_AMOUNT (raise_amount is DECIMAL(10,2)). A number input also accepts exponent
// notation such as 1e3, which the server refuses, so that gets its own message.
const PLAIN_NUMBER = /^[-+]?(\d+(\.\d*)?|\.\d+)$/;
const AMOUNT_PATTERN = /^[-+]?(\d+(\.\d{0,2})?|\.\d{1,2})$/;
const MAX_AMOUNT = 99999999.99;

// 0 means no raise and a negative amount is a decrement; both are valid.
const raiseAmountRule = (value) => {
  const text = String(value).trim();
  if (!PLAIN_NUMBER.test(text)) return 'Enter a plain number, like 5000';
  if (!AMOUNT_PATTERN.test(text)) return 'At most 2 decimal places';
  if (Math.abs(Number(text)) > MAX_AMOUNT) return 'At most ±99,999,999.99';
  return true;
};

const AppraisalForm = ({ 
  employees = [], 
  paymentMethods = [],
  mode = 'add' // 'add' or 'edit'
}) => {
  const employeeOptions = employees.map(emp => ({
    value: emp.id,
    label: `${emp.first_name} ${emp.last_name} (${emp.employee_id})`
  }));

  const paymentMethodOptions = paymentMethods.map(pm => ({
    value: pm.id,
    label: pm.name
  }));

  const currencyOptions = [
    { value: 'USD', label: 'USD' },
    { value: 'PKR', label: 'PKR' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'overdue', label: 'Overdue' }
  ];

  const ratingOptions = [
    { value: 1, label: '1 - Poor' },
    { value: 2, label: '2 - Below Average' },
    { value: 3, label: '3 - Average' },
    { value: 4, label: '4 - Above Average' },
    { value: 5, label: '5 - Excellent' }
  ];

  return (
    <div className="space-y-4">
      <FormField
        name="employee_id"
        label="Employee"
        type="select"
        required={true}
        options={employeeOptions}
        validation={{ required: 'Employee is required' }}
        searchable={true}
        placeholder="Search employees..."
        data-testid="appraisal-form-field-employee"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          name="appraisal_date"
          label="Appraisal Date"
          type="date"
          required={true}
          validation={{ required: 'Appraisal date is required' }}
          data-testid="appraisal-form-field-appraisal-date"
        />
        <FormField
          name="next_appraisal_date"
          label="Next Appraisal Date"
          type="date"
          data-testid="appraisal-form-field-next-date"
        />
      </div>

      <FormField
        name="rating"
        label="Rating"
        type="select"
        options={ratingOptions}
        validation={{
          min: { value: 1, message: 'Rating must be at least 1' },
          max: { value: 5, message: 'Rating must be at most 5' }
        }}
        data-testid="appraisal-form-field-rating"
      />

      <FormField
        name="performance_summary"
        label="Performance Summary"
        type="textarea"
        placeholder="Summary of employee performance"
      />

      <FormField
        name="goals_achieved"
        label="Goals Achieved"
        type="textarea"
        placeholder="Goals that were successfully achieved"
      />

      <FormField
        name="areas_for_improvement"
        label="Areas for Improvement"
        type="textarea"
        placeholder="Areas where the employee can improve"
      />

      <FormField
        name="recommendations"
        label="Recommendations"
        type="textarea"
        placeholder="Recommendations for future development"
      />

      <FormField
        name="manager_comments"
        label="Manager Comments"
        type="textarea"
        placeholder="Comments from the manager"
        data-testid="appraisal-form-field-comments"
      />

      <FormField
        name="status"
        label="Status"
        type="select"
        options={statusOptions}
        data-testid="appraisal-form-field-status"
      />

      {/* Salary raise section. Every appraisal carries all three fields, a raise of 0
          included: a raise saved without its currency or payment method never reached the
          salary. The server enforces the same rule. */}
      <div className="border-t pt-4">
        <h4 className="font-medium">Salary Adjustment</h4>
        <p id="salary-adjustment-hint" className="mt-1 mb-3 text-xs text-gray-500">
          Enter 0 if there is no raise.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            name="raise_amount"
            label="Raise Amount"
            type="number"
            step="0.01"
            placeholder="0.00"
            required={true}
            validation={{
              required: 'Raise amount is required',
              validate: raiseAmountRule
            }}
            aria-describedby="salary-adjustment-hint"
            data-testid="appraisal-form-field-raise-amount"
          />
          <FormField
            name="currency"
            label="Currency"
            type="select"
            required={true}
            options={currencyOptions}
            validation={{ required: 'Currency is required' }}
            data-testid="appraisal-form-field-currency"
          />
          <FormField
            name="payment_method_id"
            label="Payment Method"
            type="select"
            required={true}
            options={paymentMethodOptions}
            // The default "Select payment method" is cut off in a third-width column.
            placeholder="Select method"
            validation={{ required: 'Payment method is required' }}
            data-testid="appraisal-form-field-payment-method"
          />
        </div>
      </div>
    </div>
  );
};

export default AppraisalForm; 